<template>
  <div class="board-overview">
    <form v-on:submit.prevent="submitSearch">
      <div class="d-flex justify-content-start">
        <div>
          <div class="label">Date</div>
          <board-item-date-picker v-model="searchDateRange" :customClass="'boardOverview'" :columns="2" :is-range="true" :mode="'date'" />
        </div>

        <div class="search-items ml-2 d-flex">
          <div class="input-group">
            <input
              v-debounce="submitSearch"
              type="text"
              class="form-control input-sm"
              style="height: 30px"
              placeholder="Search..."
              v-model="search"
            />
            <div class="input-group-append" @click="submitSearch">
              <i class="fa fa-search input-group-text"></i>
            </div>
          </div>
        </div>

        <div class="d-flex flex-column ml-4">
          <div class="label">Filter</div>
          <!-- Board items Filter -->
          <div>
            <div class="badgebutton" :class="selectedCategories.length === 0 ? 'active' : ''" @click="chooseCategory('all')">All</div>
            <div
              class="badgebutton ml-2"
              :class="selectedCategories.indexOf(category.id) !== -1 ? 'active' : ''"
              v-for="category in availableCategories"
              :key="category.id"
              @click="chooseCategory(category.id)"
            >
              {{ category.name }}
            </div>
          </div>
        </div>

        <div class="ml-4">
          <div class="label">Labels</div>
          <div class="mr-2 align-self-center">
            <div class="d-flex justify-content-left">
              <div v-for="label in selectedLabels" :key="label.id">
                <div
                  class="mr-2 d-flex justify-content-between align-items-center"
                  :style="{
                    'background-color': label.color,
                    color: 'white',
                    padding: '4px 5px',
                    'border-radius': '3px',
                    'font-size': '.9rem',
                  }"
                >
                  <i v-if="label.icon" :class="label.icon" class="mr-1 ml-1"></i>
                  {{ label.title }}
                  <div>
                    <i class="ml-2 fas fa-times cursor-pointer" @click="removeLabel(label)"></i>
                  </div>
                </div>
              </div>

              <add-labels-filter @labeladded="addLabel"></add-labels-filter>
            </div>
          </div>


        </div>
      </div>
    </form>

    <div class="board-items mt-3" v-if="filteredItems && filteredItems.length">
      <table class="table table-striped fixed-header">
        <thead>
          <tr>
            <th scope="col">Category & Type</th>
            <th scope="col">Title & Description</th>
            <th scope="col">Labels</th>
            <th scope="col">Attachments</th>
            <th scope="col">Comments</th>
            <th scope="col">Subtasks</th>
            <th scope="col">Activity Date</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          <board-item-overview
            :item="item"
            v-for="(item, index) in filteredItems"
            :key="index"
            :action="navigateToBoardItem"
            :action-param="item.id"
          ></board-item-overview>
        </tbody>
      </table>
    </div>

    <div v-if="!filteredItems || !filteredItems.length" class="p-12">
      <div class="label justify-content-center d-flex align-items-center">
        <i class="far fa-search fa-2x mr-2"></i>
        No Board items found, please modify your search</div>
    </div>

    <div v-if="loading" class="d-flex align-items-center justify-content-center p-5">
      <loading-spinner v-if="loading"></loading-spinner>
    </div>

    <router-view />
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import DateRangeDto from '@/types/dateRangeDto';
  import BoardItemDatePicker from '@/components/BoardItem/BoardItemDatePicker.vue';
  import BoardItemOverview from '@/components/Overview/BoardItemOverview.vue';
  import { Watch } from 'vue-property-decorator';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';
  import { debounce } from 'vue-debounce';
  import { IBoardItemDto, ILabelDto, LabelDto } from '@/apiclient/apiclient_generated';
  import { overviewClient } from '@/apiclient/opsboardapi';
  import lodash from 'lodash';
  import AddLabelsFilter from "@/components/Labels/AddLabelsFilter.vue";

  @Component({
    components: {
      AddLabelsFilter,
      BoardItemDatePicker,
      BoardItemOverview,
      LoadingSpinner,
    },
  })
  export default class BoardOverview extends Vue {
    private search = '';
    private loading = false;
    private searchDateRange: DateRangeDto = new DateRangeDto();
    private selectedCategories: string[] = [];
    private selectedLabels: LabelDto[] = [];
    private items: IBoardItemDto[] = [];
    private labels: ILabelDto[] = [];

    async created() {
      await this.submitSearch(true);

      this.$root.$on('boardItemOverview::labelSelected', (label: LabelDto) => {
        if (!this.selectedLabels.some((_) => _.id === label.id)) {
          this.selectedLabels.push(label);
        }
      });
    }

    destroyed() {
      this.$root.$off('boardItemOverview::labelSelected');
    }

    async submitSearch(immediate = false) {
      const wait = immediate ? '0ms' : '1000ms';

      debounce(async () => {
        this.loading = true;
        this.selectedLabels = [];
        this.selectedCategories = [];
        this.items = await overviewClient.getBoardOverview(
          this.$route.params.id,
          this.searchDateRange.start,
          this.searchDateRange.end,
          true,
          this.search
        );
        this.loading = false;
      }, wait)();
    }

    chooseCategory(category) {
      if (category === 'all') {
        this.selectedCategories = [];
      } else {
        const categoryIndex = this.selectedCategories.indexOf(category);
        if (categoryIndex === -1) {
          this.selectedCategories.push(category);
        } else {
          this.selectedCategories.splice(categoryIndex, 1);
        }
      }
    }

    get filteredItems(): IBoardItemDto[] {
      let items = this.items;
      if (this.selectedCategories?.length) {
        items = this.items.filter((item) => this.selectedCategories.indexOf(item.boardItemCategory?.id) !== -1);
      }

      if (this.selectedLabels?.length) {
        items = items.filter((item) => item.labels.some((label) => this.selectedLabels.some((_) => _.id === label.id)));
      }

      return items;
    }

    get availableCategories() {
      if (!this.items?.length) {
        return [];
      }

      return lodash.uniqBy(lodash.map(this.items, 'boardItemCategory'), (_) => _?.id);
    }

    private removeLabel(label: LabelDto) {
      const index = this.selectedLabels.findIndex((_) => _.id === label.id);

      if (index >= 0) {
        this.selectedLabels.splice(index, 1);
      }
    }

    get filteredLabels() {
      return this.labels;
    }

    async addLabel(label) {
      if (!this.selectedLabels.some((_) => _.id === label.id)) {
        this.selectedLabels.push(label);
      }
    }

    @Watch('searchDateRange')
    changeFromDate() {
      this.submitSearch(true);
    }

    navigateToBoardItem(id) {
      this.$router.push({
        name: 'boardItemOverview',
        params: { boardItemOverviewId: id },
      });
    }
  }
</script>

<style lang="scss">
  .board-items {
    table tbody tr:hover td,
    table tbody tr:hover th {
      transition: background-color ease 0.4s, margin-top ease 0.4s;
      background-color: #e0edf1;
      cursor: pointer;
    }
  }
</style>

<style scoped lang="scss">
  .search-items {
    padding-top: 27px;
    i {
      padding-top: 10px;
    }
    i:hover {
      cursor: pointer;
    }
  }

  .board-items {
    height: 70vh;
    padding: 0 4px;
    overflow-y: auto;
  }

  .board-items::-webkit-scrollbar {
    width: 8px;
  }

  .board-items::-webkit-scrollbar-track {
    background: #ddd;
    border-radius: 5px;
  }

  .board-items::-webkit-scrollbar-thumb {
    background: #999;
    border: 2px solid #ddd;
    border-radius: 5px;
  }
</style>
